import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { Provider } from "react-redux";
import { store } from "./redux/store";
import {
  useGetHomepageSettingsQuery,
  useGetSpecialOffersQuery,
  useGetServicesQuery,
  useGetTestimonialsQuery,
} from "./redux/services/api";
import { LanguageProvider } from "./contexts/LanguageContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { trackHomePage } from "./utils/analytics";
import ProtectedRoute from "./components/ProtectedRoute";
import DashboardLayout from "./components/DashboardLayout";
import Header from "./components/Header";
import Hero from "./components/Hero";
import IntroSection from "./components/IntroSection";
import GeometricPattern from "./components/GeometricPattern";
import SpecialOffersSection from "./components/SpecialOffers";
import ServicesSection from "./components/Services";
import ContactUs from "./components/ContactUs";
import Footer from "./components/Footer";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import HomepageSettings from "./pages/HomepageSettings";
import SpecialOffers from "./pages/SpecialOffers";
import AddSpecialOffer from "./pages/AddSpecialOffer";
import EditSpecialOffer from "./pages/EditSpecialOffer";
import ServicesCategories from "./pages/ServicesCategories";
import AddServicesCategory from "./pages/AddServicesCategory";
import EditServicesCategory from "./pages/EditServicesCategory";
import Services from "./pages/Services";
import AddService from "./pages/AddService";
import EditService from "./pages/EditService";
import OfferDetail from "./pages/OfferDetail";
import ServiceDetail from "./pages/ServiceDetail";
import ServicesPage from "./pages/ServicesPage";
import OffersPage from "./pages/OffersPage";
import SearchPage from "./pages/SearchPage";
import Testimonials from "./components/Testimonials";
import TestimonialsPage from "./pages/Testimonials";
import AddTestimonial from "./pages/AddTestimonial";
import EditTestimonial from "./pages/EditTestimonial";
import Analytics from "./pages/Analytics";
import VisitorDetails from "./pages/VisitorDetails";
import HeadTags from "./components/HeadTags";
import Links from "./pages/Links";
import FloatingButtons from "./components/FloatingButtons";
import "./App.css";
import "./styles/light-mode.css";

function HomePage() {
  // Use RTK Query hooks to get loading states for all homepage data
  const { isLoading: homepageSettingsLoading } = useGetHomepageSettingsQuery();
  const { isLoading: offersLoading } = useGetSpecialOffersQuery();
  const { isLoading: servicesLoading } = useGetServicesQuery();
  const { isLoading: testimonialsLoading } = useGetTestimonialsQuery();

  // Check if any of the main data is still loading
  const isLoading =
    homepageSettingsLoading ||
    offersLoading ||
    servicesLoading ||
    testimonialsLoading;

  // Track home page visit
  useEffect(() => {
    trackHomePage();
  }, []);

  if (isLoading) {
    return (
      <div className="homepage-loading">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading content...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="homepage-container">
      <Header />
      <Hero />
      <main className="main-content">
        <IntroSection />
        <div className="section-separator"></div>
        <SpecialOffersSection />
        <div className="section-separator"></div>
        <ServicesSection />
        <div className="section-separator"></div>
        <Testimonials />
        <div className="section-separator"></div>
        <ContactUs />
      </main>
      <Footer />
      <FloatingButtons />
    </div>
  );
}

function App() {
  return (
    <Provider store={store}>
      <LanguageProvider>
        <Router>
          <ThemeProvider>
            <HeadTags />
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/offer/:id" element={<OfferDetail />} />
              <Route path="/service/:id" element={<ServiceDetail />} />
              <Route path="/services" element={<ServicesPage />} />
              <Route path="/offers" element={<OffersPage />} />
              <Route path="/search" element={<SearchPage />} />
              <Route path="/login" element={<Login />} />
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <DashboardLayout>
                      <Dashboard />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/homepage"
                element={
                  <ProtectedRoute>
                    <DashboardLayout>
                      <HomepageSettings />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/special-offers"
                element={
                  <ProtectedRoute>
                    <DashboardLayout>
                      <SpecialOffers />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/special-offers/add"
                element={
                  <ProtectedRoute>
                    <DashboardLayout>
                      <AddSpecialOffer />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/special-offers/edit/:id"
                element={
                  <ProtectedRoute>
                    <DashboardLayout>
                      <EditSpecialOffer />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/services-categories"
                element={
                  <ProtectedRoute>
                    <DashboardLayout>
                      <ServicesCategories />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/services-categories/add"
                element={
                  <ProtectedRoute>
                    <DashboardLayout>
                      <AddServicesCategory />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/services-categories/edit/:id"
                element={
                  <ProtectedRoute>
                    <DashboardLayout>
                      <EditServicesCategory />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/services"
                element={
                  <ProtectedRoute>
                    <DashboardLayout>
                      <Services />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/services/add"
                element={
                  <ProtectedRoute>
                    <DashboardLayout>
                      <AddService />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/services/edit/:id"
                element={
                  <ProtectedRoute>
                    <DashboardLayout>
                      <EditService />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/testimonials"
                element={
                  <ProtectedRoute>
                    <DashboardLayout>
                      <TestimonialsPage />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/testimonials/add"
                element={
                  <ProtectedRoute>
                    <DashboardLayout>
                      <AddTestimonial />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/testimonials/edit/:id"
                element={
                  <ProtectedRoute>
                    <DashboardLayout>
                      <EditTestimonial />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/analytics"
                element={
                  <ProtectedRoute>
                    <DashboardLayout>
                      <Analytics />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/visitor-details"
                element={
                  <ProtectedRoute>
                    <DashboardLayout>
                      <VisitorDetails />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/links"
                element={
                  <ProtectedRoute>
                    <DashboardLayout>
                      <Links />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </ThemeProvider>
        </Router>
      </LanguageProvider>
    </Provider>
  );
}

export default App;
